// El nombre de una acción de sólo ícono, al pasar el mouse. Vivía adentro de
// `edicionEnElVisor/ModoCapas.tsx` y se sacó cuando la ficha de Tienda online necesitó el mismo para
// "Deshacer" (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas).
//
// Sólo con mouse (`pointerType === 'mouse'`) o con foco de teclado: en el teléfono un toque no lo abre.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// ── El nombre de una acción al pasar el mouse ─────────────────────────────────
// Los íconos de cada fila (guardar, editar con IA, ocultar) dicen qué hacen
// (ADR-2026-10-20-las-acciones-de-la-capa-dicen-su-nombre). Va por portal y con posición
// fija: la lista vive en una caja con `overflow-hidden` que lo cortaría en la primera fila.

/** Lo mínimo que el rótulo queda separado del borde de la pantalla. */
const MARGEN_DEL_ROTULO = 8;

export const ConRotulo: React.FC<{ rotulo: string; children: ReactNode }> = ({ rotulo, children }) => {
  const [ancla, setAncla] = useState<{ x: number; y: number } | null>(null);
  // Centrado sobre el botón, pero adentro de la pantalla: el panel está pegado al borde
  // derecho y "Guardar en Elementos de marca" se salía. Se mide antes de pintar.
  const rotuloRef = useRef<HTMLSpanElement | null>(null);
  // La posición se calcula con las medidas, sin `translate`: `animate-fade-in` anima
  // `transform` y queda aplicado al terminar, así que pisaba el `-translate-y-full` y el
  // rótulo bajaba sobre el botón.
  const [posicion, setPosicion] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = rotuloRef.current;
    if (!ancla || !el) return setPosicion(null);
    const ancho = el.offsetWidth;
    const max = window.innerWidth - ancho - MARGEN_DEL_ROTULO;
    setPosicion({
      left: Math.max(MARGEN_DEL_ROTULO, Math.min(ancla.x - ancho / 2, max)),
      top: Math.max(MARGEN_DEL_ROTULO, ancla.y - el.offsetHeight - 6),
    });
  }, [ancla, rotulo]);
  const mostrar = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    setAncla({ x: r.left + r.width / 2, y: r.top });
  };
  return (
    <span
      className="inline-flex shrink-0"
      onPointerEnter={(e) => e.pointerType === 'mouse' && mostrar(e.currentTarget)}
      onPointerLeave={() => setAncla(null)}
      onPointerDown={() => setAncla(null)}
      onFocus={(e) => mostrar(e.currentTarget)}
      onBlur={() => setAncla(null)}
    >
      {children}
      {ancla &&
        createPortal(
          <span
            ref={rotuloRef}
            role="tooltip"
            className="pointer-events-none fixed z-[300] whitespace-nowrap rounded-md bg-[#1C1D22] px-2 py-1 text-[12px] font-medium text-white shadow-[0_6px_18px_-6px_rgba(0,0,0,0.6)] ring-1 ring-white/10 animate-fade-in"
            style={{ left: posicion?.left ?? ancla.x, top: posicion?.top ?? ancla.y, visibility: posicion ? 'visible' : 'hidden' }}
          >
            {rotulo}
          </span>,
          document.body,
        )}
    </span>
  );
};
